import { useEffect, useState, type FormEvent } from 'react';
import { formatPrice, plural } from '../format';
import { adminApi, errorText, type CourseSummary, type ProductRow } from './api';
import { Card, Field, SaveBar, Tag, confirmAction, useSaver } from './ui';

/** Produkte und Preise (Konzept 5.3): Ein Produkt schaltet einen oder mehrere Kurse frei. */
export function ProductsPage() {
  const [products, setProducts] = useState<ProductRow[] | null>(null);
  const [courses, setCourses] = useState<CourseSummary[]>([]);
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void adminApi.listProducts().then(setProducts).catch(() => setProducts([]));
    void adminApi.listCourses().then(setCourses).catch(() => undefined);
  }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      const p = await adminApi.createProduct(title.trim());
      setProducts([...(products ?? []), p]);
      setTitle('');
    } catch (err) {
      setError(errorText(err));
    }
  }

  const replace = (p: ProductRow) => setProducts((list) => (list ?? []).map((x) => (x.id === p.id ? p : x)));
  const remove = (id: string) => setProducts((list) => (list ?? []).filter((x) => x.id !== id));

  return (
    <>
      <div className="adm-head">
        <div>
          <h1>Produkte & Preise</h1>
          <p className="muted">Zwischen Kurs und Kunde steht das Produkt: ein Kurs, ein Paket aus mehreren oder ein Gratis-Zugang. Jedes Produkt hat einen Verkaufslink.</p>
        </div>
      </div>
      <Card as="form" onSubmit={create} title="Neues Produkt">
        <div className="row">
          <input type="text" placeholder="Name des Produkts, z. B. „Komplettpaket“" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
          <button type="submit" className="button" disabled={!title.trim()}>Anlegen</button>
        </div>
        {error && <div className="error">{error}</div>}
      </Card>
      {products === null ? <p className="muted">Wird geladen …</p> : products.length === 0 ? <p className="muted">Noch kein Produkt.</p> : products.map((p) => <ProductCard key={p.id} product={p} courses={courses} onChanged={replace} onDeleted={() => remove(p.id)} />)}
    </>
  );
}

const STATUS = { active: 'verkaufbar', hidden: 'versteckt', archived: 'archiviert' } as const;

function ProductCard({ product: p, courses, onChanged, onDeleted }: { product: ProductRow; courses: CourseSummary[]; onChanged: (p: ProductRow) => void; onDeleted: () => void }) {
  const toForm = (x: ProductRow) => ({ title: x.title, slug: x.slug, description: x.description ?? '', price: (x.priceCents / 100).toFixed(2).replace('.', ','), status: x.status, accessMonths: x.accessMonths ? String(x.accessMonths) : '', courseIds: [...x.courseIds].sort() });
  const [form, setForm] = useState(toForm(p));
  const [open, setOpen] = useState(false);
  const saver = useSaver();
  useEffect(() => setForm(toForm(p)), [p]);
  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(p));

  function submit(e: FormEvent) {
    e.preventDefault();
    const cents = Math.round(Number(form.price.replace(/\./g, '').replace(',', '.')) * 100);
    if (!Number.isFinite(cents) || cents < 0) return saver.setError('Preis bitte als Zahl, zum Beispiel 297 oder 49,90.');
    void saver.run(async () => onChanged(await adminApi.updateProduct(p.id, { title: form.title, slug: form.slug, description: form.description || null, priceCents: cents, status: form.status, accessMonths: form.accessMonths ? Number(form.accessMonths) : null, courseIds: form.courseIds })));
  }

  return (
    <Card
      as="form"
      onSubmit={submit}
      title={
        <button type="button" className="card-toggle" onClick={() => setOpen((v) => !v)}>
          <span className={`card-toggle__chevron${open ? ' card-toggle__chevron--open' : ''}`} aria-hidden="true" />
          {p.title} <span className="muted" style={{ fontWeight: 400 }}>· {formatPrice(p.priceCents, p.currency)}</span>
        </button>
      }
      right={
        <span className="row-actions">
          <span className="muted small">{plural(p.customers, 'Kunde', 'Kunden')}</span>
          <Tag tone={p.status === 'active' ? 'ok' : p.status === 'hidden' ? 'warn' : 'muted'}>{STATUS[p.status]}</Tag>
        </span>
      }
    >
      {open && (
        <>
          <div className="grid2">
            <Field label="Name" id={`p-title-${p.id}`}>
              <input id={`p-title-${p.id}`} type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required maxLength={200} />
            </Field>
            <Field label="Preis in Euro" id={`p-price-${p.id}`} hint="0 = kostenlos (Anmeldung nur mit E-Mail).">
              <input id={`p-price-${p.id}`} type="text" inputMode="decimal" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </Field>
            <Field label="Verkaufslink" id={`p-slug-${p.id}`} hint={`${window.location.origin}/kaufen/${form.slug || '…'}`}>
              <input id={`p-slug-${p.id}`} type="text" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} maxLength={80} pattern="[a-z0-9][a-z0-9-]*" />
            </Field>
            <Field label="Status" id={`p-status-${p.id}`} hint="Versteckt = nur von Hand freischaltbar, nicht kaufbar.">
              <select id={`p-status-${p.id}`} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as ProductRow['status'] })}>
                <option value="active">Verkaufbar</option>
                <option value="hidden">Versteckt</option>
                <option value="archived">Archiviert</option>
              </select>
            </Field>
            <Field label="Zugang befristet auf (Monate)" id={`p-months-${p.id}`} hint="Leer = unbefristet.">
              <input id={`p-months-${p.id}`} type="number" min={1} max={600} value={form.accessMonths} onChange={(e) => setForm({ ...form, accessMonths: e.target.value })} />
            </Field>
          </div>
          <Field label="Beschreibung" id={`p-desc-${p.id}`}>
            <textarea id={`p-desc-${p.id}`} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={5000} />
          </Field>
          <Field label="Schaltet diese Kurse frei">
            {courses.length === 0 ? (
              <span className="muted small">Noch keine Kurse angelegt.</span>
            ) : (
              <div className="checks">
                {courses.map((c) => (
                  <label key={c.id} className="check">
                    <input type="checkbox" checked={form.courseIds.includes(c.id)} onChange={(e) => setForm({ ...form, courseIds: (e.target.checked ? [...form.courseIds, c.id] : form.courseIds.filter((x) => x !== c.id)).sort() })} />
                    <span>
                      {c.title.replace('\n', ' ')} {c.status !== 'published' && <span className="muted small">({c.status === 'draft' ? 'Entwurf' : 'archiviert'})</span>}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </Field>
          <SaveBar saver={saver} dirty={dirty}>
            <button type="button" className="link-button link-button--danger" onClick={() => { if (confirmAction(`Produkt „${p.title}“ löschen?`)) void adminApi.deleteProduct(p.id).then(onDeleted).catch((e: unknown) => saver.setError(errorText(e))); }}>
              Löschen
            </button>
          </SaveBar>
        </>
      )}
    </Card>
  );
}
